import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { useMemo } from 'react';
import { MultiSelectAll } from 'src/components/common/MultiSelect/MultiSelectAll';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { useEventsAccordionSelectedLegend } from 'src/components/Events/EventListing/useEventsAccordionSelectedLegend';
import { BulkActionGroup } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import {
  Content,
  getContent,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { MobileListingDrawerZIndex } from 'src/contexts/MobileListingDrawerContext/MobileListingDrawerContext.constants';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Button, Stack } from 'src/core/ui';
import { getFlattenedListings } from 'src/modals/MergeListings/utils';
import { ChevronRightIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { ActionOutboxEntityType, EventWithData } from 'src/WebApiController';

import * as styles from './BulkActionsContent.css';

export const BulkActionsDrawer = ({
  show,
  event,
  entityType,
  flattenedIdsOverride,
  actionGroups,
  onClose,
}: {
  show: boolean;
  event?: EventWithData;
  entityType: ActionOutboxEntityType;
  flattenedIdsOverride?: string[];
  actionGroups: BulkActionGroup[];
  onClose: () => void;
}) => {
  const listingText = useContent(ContentId.Listing);
  const listingsText = useContent(ContentId.Listings);
  const saleText = useContent(ContentId.Sale);
  const salesText = useContent(ContentId.Sales);
  const purchaseText = useContent(ContentId.Purchase);
  const purchasesText = useContent(ContentId.Purchases);

  const { singleElementLabel, multipleElementsLabel } = useMemo(() => {
    if (entityType === ActionOutboxEntityType.Purchase) {
      return {
        singleElementLabel: purchaseText,
        multipleElementsLabel: purchasesText,
      };
    }
    if (entityType === ActionOutboxEntityType.Sale) {
      return {
        singleElementLabel: saleText,
        multipleElementsLabel: salesText,
      };
    }
    return {
      singleElementLabel: listingText,
      multipleElementsLabel: listingsText,
    };
  }, [
    entityType,
    listingText,
    listingsText,
    purchaseText,
    purchasesText,
    saleText,
    salesText,
  ]);

  const customCountLabel = useEventsAccordionSelectedLegend({
    singleElementLabel,
    multipleElementsLabel,
  });

  const [nonDestructiveGroups, destructiveGroup] = useMemo(() => {
    const nonDestructiveGroups = actionGroups.filter(
      (group) => !group.destructive
    );
    const destructiveGroup = actionGroups.find((group) => group.destructive);
    return [nonDestructiveGroups, destructiveGroup];
  }, [actionGroups]);

  return (
    <Drawer.Root
      id={`BulkActionsDrawer-${entityType}`}
      opened={show}
      onClose={onClose}
      size="100%"
      position="right"
      className={globalMobileWrapper}
      zIndex={MobileListingDrawerZIndex.ListingActions}
    >
      <Drawer.Content className={styles.drawerContent}>
        <Drawer.Header className={styles.drawerHeader}>
          <BulkActionsHeader
            event={event}
            customCountLabel={customCountLabel}
            flattenedIdsOverride={flattenedIdsOverride}
            entityType={entityType}
            onClose={onClose}
          />
        </Drawer.Header>
        <Drawer.Body
          className={styles.drawerBody}
          style={{
            backgroundColor: vars.color.backgroundSecondary,
          }}
        >
          <Stack
            width="full"
            height="full"
            direction="column"
            className={styles.contentWrapper}
          >
            <BulkActionContent actionGroups={nonDestructiveGroups} />
            <DestructiveActionsContent destructiveGroup={destructiveGroup} />
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};

const BulkActionsHeader = ({
  event,
  entityType,
  customCountLabel,
  flattenedIdsOverride,
  onClose,
}: {
  event?: EventWithData;
  entityType: ActionOutboxEntityType;
  customCountLabel?: string;
  flattenedIdsOverride?: string[];
  onClose: () => void;
}) => {
  const { totalItemsSelected, getSelection } = useMultiSelectionContext();
  const selectedItems = useMemo(
    () => getSelection(event?.event?.virtId),
    [event?.event?.virtId, getSelection]
  );
  const contentContext = useContentContext();
  const selectedText = useContent(ContentId.Selected);
  const singleItemSelected = useMemo(() => {
    if (totalItemsSelected === 1) {
      const id = selectedItems.items.itemIds[0];
      if (entityType === ActionOutboxEntityType.Listing) {
        const listings = getFlattenedListings(event?.entities?.listings);
        const singleListing = listings[0].find((l) => l.id.toString() === id);
        if (singleListing?.seating) {
          return <SeatingInfo {...singleListing.seating} />;
        }
      }

      const entity =
        entityType === ActionOutboxEntityType.Sale
          ? event?.entities.sales?.find((s) => s.id.toString() === id)
          : event?.entities.ticketGroups?.find((p) => p.id.toString() === id);

      if (entity?.seating) {
        return <SeatingInfo {...entity.seating} />;
      }
    }

    return null;
  }, [
    entityType,
    event?.entities?.listings,
    event?.entities.sales,
    event?.entities.ticketGroups,
    selectedItems.items.itemIds,
    totalItemsSelected,
  ]);

  const customSelectedText = useMemo(() => {
    let customLabel = undefined;
    if (entityType) {
      switch (entityType) {
        case ActionOutboxEntityType.Listing:
          customLabel = getContent(
            totalItemsSelected > 1 ? ContentId.Listing : ContentId.Listings,
            contentContext
          );
          break;
        case ActionOutboxEntityType.Sale:
          customLabel = getContent(
            totalItemsSelected > 1 ? ContentId.Sale : ContentId.Sales,
            contentContext
          );
          break;
        case ActionOutboxEntityType.Purchase:
          customLabel = getContent(
            totalItemsSelected > 1 ? ContentId.Purchase : ContentId.Purchases,
            contentContext
          );
          break;
        case ActionOutboxEntityType.TicketGroup:
          customLabel = getContent(
            totalItemsSelected > 1
              ? ContentId.TicketGroup
              : ContentId.TicketGroups,
            contentContext
          );
          break;
      }
    }

    return `${customLabel} ${selectedText}`.toLocaleLowerCase();
  }, [contentContext, entityType, selectedText, totalItemsSelected]);

  return (
    <MobileHeaderBar
      onBack={onClose}
      title={
        <>
          {singleItemSelected ? (
            <Stack
              gap="m"
              direction="row"
              alignItems="center"
              className={styles.contentTitle}
            >
              <span>
                <Content id={ContentId.Editing} />:
              </span>{' '}
              {singleItemSelected}
            </Stack>
          ) : (
            <MultiSelectAll
              displayMode="count-only"
              customSelectedText={customSelectedText}
              customCountLabel={customCountLabel}
              flattenedIdsOverride={flattenedIdsOverride}
            />
          )}
        </>
      }
      actions={
        <Button shape="rect" variant="outlineGray" onClick={onClose}>
          <Content id={ContentId.Done} />
        </Button>
      }
    />
  );
};

const BulkActionContent = ({
  actionGroups,
}: {
  actionGroups: BulkActionGroup[];
}) => {
  return (
    <Stack direction="column" gap="l" height="full">
      {actionGroups.map((group) => (
        <Stack key={group.id} direction="column" gap="m">
          <h4 className={styles.sectionTitle}>{group.label}</h4>
          <Stack direction="column" gap="s">
            {group.actions.map((action, index) => (
              <Stack
                role="button"
                tabIndex={index}
                key={action.title}
                width="full"
                alignItems="center"
                justifyContent="spaceBetween"
                className={styles.actionItem}
                onClick={action.onClick}
                style={{ cursor: 'pointer' }}
              >
                <Content id={action.title} />
                <ChevronRightIcon size={vars.iconSize.s} />
              </Stack>
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

const DestructiveActionsContent = ({
  destructiveGroup,
}: {
  destructiveGroup?: BulkActionGroup;
}) => {
  if (!destructiveGroup) {
    return null;
  }

  return (
    <Stack direction="column" gap="s" style={{ marginTop: vars.spacing.m }}>
      {destructiveGroup.actions.map((action, index) => (
        <Stack
          role="button"
          tabIndex={index}
          key={action.title}
          width="full"
          alignItems="center"
          justifyContent="spaceBetween"
          className={clsx(styles.actionItem, styles.destructive)}
          onClick={action.onClick}
          style={{ cursor: 'pointer' }}
        >
          <Content id={action.title} />
          <ChevronRightIcon size={vars.iconSize.s} />
        </Stack>
      ))}
    </Stack>
  );
};
